import { getOrCreateDeviceId } from '@/services/localDb/device';
import { deleteRecord, getRecord, listRecords, saveRecord } from '@/services/localDb';
import {
  OFFLINE_SESSION_STATE_KEY,
  removePersistedAppState,
} from '@/services/offline/appState';
import { DEFAULT_WAITER_TPV_PERMISSIONS, normalizeTpvPermissions } from '@/utils/tpv_permissions';

const LAST_RESTAURANT_CONTEXT_KEY = 'ewu_tpv_last_restaurant_context';
import { readTabContext, writeTabContext } from '@/services/auth/partnerSessionStorage';

const OFFLINE_SESSION_STORAGE_KEY = 'ewu_tpv_offline_unlocked_session';
const OFFLINE_USER_STORE = 'offline_users';
const OFFLINE_ACCESS_SECURITY_MIGRATION_KEY = 'ewu_tpv_offline_access_security_migration_v2';
const PBKDF2_ITERATIONS = 120000;
const PBKDF2_HASH = 'SHA-256';
const OFFLINE_PIN_MAX_ATTEMPTS = 5;
const OFFLINE_PIN_LOCK_BASE_MS = 30000;
const OFFLINE_PIN_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

let offlineSessionCache = null;
let offlineSessionHydrated = false;
let offlineSessionHydrationPromise = null;
let offlineSecurityMigrationPromise = null;

function nowIso() {
  return new Date().toISOString();
}

function normalizeId(value) {
  return value === undefined || value === null || value === '' ? null : String(value);
}

function readLocalStorageFlag(key) {
  if (typeof localStorage === 'undefined') return null;

  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocalStorageFlag(key, value) {
  if (typeof localStorage === 'undefined') return;

  try {
    localStorage.setItem(key, value);
  } catch {
    // Ignore blocked storage.
  }
}

function parseDateToMs(value) {
  const timestamp = new Date(value || 0).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function formatWaitTime(ms) {
  const seconds = Math.max(1, Math.ceil(ms / 1000));
  if (seconds < 60) {
    return `${seconds} segundo${seconds === 1 ? '' : 's'}`;
  }

  const minutes = Math.ceil(seconds / 60);
  return `${minutes} minuto${minutes === 1 ? '' : 's'}`;
}

function createOfflineAccessError(message, code, details = {}) {
  const error = new Error(message);
  error.code = code;
  Object.assign(error, details);
  return error;
}

function buildOfflineAccessExpiry(record) {
  const lastOnlineLoginAt = parseDateToMs(record?.last_successful_online_login_at);
  if (!lastOnlineLoginAt) return 0;
  return lastOnlineLoginAt + OFFLINE_PIN_MAX_AGE_MS;
}

function isOfflineAccessExpired(record) {
  const expiresAt = buildOfflineAccessExpiry(record);
  return Boolean(expiresAt && expiresAt <= Date.now());
}

function getOfflineLockRemainingMs(record) {
  const lockedUntil = parseDateToMs(record?.offline_unlock_locked_until);
  if (!lockedUntil || lockedUntil <= Date.now()) return 0;
  return lockedUntil - Date.now();
}

function sanitizeOfflineUserSecretFields(record = {}) {
  return {
    ...record,
    encrypted_password: null,
    password_iv: null,
  };
}

async function migrateOfflineUserSecurityState() {
  if (readLocalStorageFlag(OFFLINE_ACCESS_SECURITY_MIGRATION_KEY) === 'done') {
    return;
  }

  if (!offlineSecurityMigrationPromise) {
    offlineSecurityMigrationPromise = (async () => {
      const records = await listRecords(OFFLINE_USER_STORE);
      const updates = records
        .filter((record) => record?.encrypted_password || record?.password_iv)
        .map((record) => saveRecord(OFFLINE_USER_STORE, {
          ...sanitizeOfflineUserSecretFields(record),
          updated_at: nowIso(),
        }));

      await Promise.all(updates);
      writeLocalStorageFlag(OFFLINE_ACCESS_SECURITY_MIGRATION_KEY, 'done');
    })();
  }

  try {
    await offlineSecurityMigrationPromise;
  } finally {
    offlineSecurityMigrationPromise = null;
  }
}

function emitOfflineSessionChanged() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('ewu-offline-session-changed'));
}

function encodeBase64(bytes) {
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
}

function decodeBase64(base64) {
  const binary = atob(base64);
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}

function hasWebCrypto() {
  return typeof window !== 'undefined'
    && typeof window.crypto !== 'undefined'
    && typeof window.crypto.subtle !== 'undefined'
    && typeof window.crypto.getRandomValues === 'function';
}

function maskUsernameOrEmail(value) {
  if (!value) return '';
  const text = String(value).trim();
  if (!text.includes('@')) {
    return text.length <= 2 ? `${text[0] || ''}*` : `${text.slice(0, 2)}***`;
  }
  const [name, domain] = text.split('@');
  const visible = name.slice(0, 2);
  return `${visible}${'*'.repeat(Math.max(1, name.length - 2))}@${domain}`;
}

function buildOfflineUserId({ userId, restaurantId, deviceId }) {
  return `${normalizeId(restaurantId)}:${normalizeId(deviceId)}:${normalizeId(userId)}`;
}

function buildRole(user) {
  if (user?.is_admin) return 'admin';
  if (user?.is_partner) return 'partner';
  return 'waiter';
}

function buildOfflineUserRecord(user, existingRecord = {}, deviceId = getOrCreateDeviceId()) {
  const safeExistingRecord = existingRecord || {};
  const restaurantId = normalizeId(user?.restaurant_id || user?.restaurant?.id);
  const userId = normalizeId(user?.user_id || user?.id);
  const now = nowIso();

  return {
    id: buildOfflineUserId({ userId, restaurantId, deviceId }),
    user_id: userId,
    restaurant_id: restaurantId,
    restaurant_name: user?.restaurant_name || user?.restaurant?.name || safeExistingRecord.restaurant_name || '',
    device_id: normalizeId(deviceId),
    login_identifier: user?.login_identifier
      || user?.email
      || user?.username
      || safeExistingRecord.login_identifier
      || '',
    display_name: user?.display_name || user?.name || user?.username || user?.email || safeExistingRecord.display_name || 'Usuario',
    username_or_email_masked: user?.username_or_email_masked
      || maskUsernameOrEmail(user?.email || user?.username || safeExistingRecord.username_or_email_masked || ''),
    role: user?.role || buildRole(user),
    permissions: normalizeTpvPermissions(user?.permissions || user?.tpv_permissions, DEFAULT_WAITER_TPV_PERMISSIONS),
    is_partner: Boolean(user?.is_partner),
    subscription_plan: user?.subscription_plan || user?.restaurant?.subscription_plan || safeExistingRecord.subscription_plan || 'ewu_tpv',
    user_allowed_to_cancel: Boolean(user?.user_allowed_to_cancel),
    can_login_offline: Boolean(safeExistingRecord.can_login_offline),
    last_successful_online_login_at: now,
    last_offline_login_at: safeExistingRecord.last_offline_login_at || null,
    pin_hash: safeExistingRecord.pin_hash || null,
    pin_salt: safeExistingRecord.pin_salt || null,
    encrypted_password: null,
    password_iv: null,
    failed_pin_attempts: 0,
    last_failed_pin_attempt_at: null,
    offline_unlock_locked_until: null,
    created_at: safeExistingRecord.created_at || now,
    updated_at: now,
  };
}

async function derivePinHash(pin, saltBase64) {
  if (!hasWebCrypto()) {
    throw new Error('Web Crypto no está disponible en este navegador.');
  }

  const encoder = new TextEncoder();
  const keyMaterial = await window.crypto.subtle.importKey(
    'raw',
    encoder.encode(String(pin)),
    'PBKDF2',
    false,
    ['deriveBits']
  );

  const derivedBits = await window.crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: decodeBase64(saltBase64),
      iterations: PBKDF2_ITERATIONS,
      hash: PBKDF2_HASH,
    },
    keyMaterial,
    256
  );

  return encodeBase64(new Uint8Array(derivedBits));
}

async function derivePinAesKey(pin, saltBase64) {
  if (!hasWebCrypto()) {
    throw new Error('Web Crypto no está disponible en este navegador.');
  }

  const encoder = new TextEncoder();
  const keyMaterial = await window.crypto.subtle.importKey(
    'raw',
    encoder.encode(String(pin)),
    'PBKDF2',
    false,
    ['deriveKey']
  );

  return window.crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: decodeBase64(saltBase64),
      iterations: PBKDF2_ITERATIONS,
      hash: PBKDF2_HASH,
    },
    keyMaterial,
    {
      name: 'AES-GCM',
      length: 256,
    },
    false,
    ['encrypt', 'decrypt']
  );
}

async function decryptWithPin({ encryptedValue, pin, saltBase64, ivBase64 }) {
  const key = await derivePinAesKey(pin, saltBase64);
  const decrypted = await window.crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: decodeBase64(ivBase64) },
    key,
    decodeBase64(encryptedValue)
  );
  const decoder = new TextDecoder();
  return decoder.decode(decrypted);
}

function createSaltBase64() {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return encodeBase64(bytes);
}

function readJsonStorage(key, source = sessionStorage) {
  try {
    const raw = source.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    console.warn('Unable to read EWU offline storage value:', error);
    return null;
  }
}

function cloneSession(session) {
  return session ? JSON.parse(JSON.stringify(session)) : null;
}

function persistOfflineSession(session) {
  if (typeof sessionStorage === 'undefined') return;

  try {
    sessionStorage.setItem(OFFLINE_SESSION_STORAGE_KEY, JSON.stringify(session));
  } catch (error) {
    console.warn('Unable to persist EWU offline unlocked session:', error);
  }
}

function removePersistedOfflineSession() {
  if (typeof sessionStorage === 'undefined') return;

  try {
    sessionStorage.removeItem(OFFLINE_SESSION_STORAGE_KEY);
  } catch (error) {
    console.warn('Unable to clear EWU offline unlocked session:', error);
  }
}

function isValidOfflineSession(session) {
  return Boolean(
    session?.mode === 'offline'
    && session?.restaurant_id
    && session?.user_id
    && session?.source === 'offline_unlock'
  );
}

async function loadOfflineUnlockedSession() {
  const storedSession = readJsonStorage(OFFLINE_SESSION_STORAGE_KEY, sessionStorage);

  await removePersistedAppState(OFFLINE_SESSION_STATE_KEY);

  return isValidOfflineSession(storedSession) ? storedSession : null;
}

export async function hydrateOfflineSessionStore() {
  if (offlineSessionHydrated) {
    return offlineSessionCache;
  }

  if (!offlineSessionHydrationPromise) {
    offlineSessionHydrationPromise = (async () => {
      offlineSessionCache = await loadOfflineUnlockedSession();
      offlineSessionHydrated = true;
      return offlineSessionCache;
    })();
  }

  try {
    return await offlineSessionHydrationPromise;
  } finally {
    offlineSessionHydrationPromise = null;
  }
}

export function supportsOfflinePinSetup() {
  return hasWebCrypto();
}

export async function saveOfflineUserProfile(user, options = {}) {
  if (!user) return null;

  await migrateOfflineUserSecurityState();

  const deviceId = normalizeId(options.deviceId || getOrCreateDeviceId());
  const restaurantId = normalizeId(user?.restaurant_id || user?.restaurant?.id);
  const userId = normalizeId(user?.user_id || user?.id);
  if (!deviceId || !restaurantId || !userId) return null;

  const recordId = buildOfflineUserId({ userId, restaurantId, deviceId });
  const existingRecord = await getRecord(OFFLINE_USER_STORE, recordId);
  const record = buildOfflineUserRecord({
    ...user,
    login_identifier: options.loginIdentifier || user?.login_identifier || user?.email || user?.username || existingRecord?.login_identifier || '',
  }, existingRecord, deviceId);
  await saveRecord(OFFLINE_USER_STORE, record);
  persistLastActiveRestaurantContext({
    restaurant_id: restaurantId,
    restaurant_name: record.restaurant_name,
  });
  return record;
}

export async function enableOfflineLoginForUser({ user, pin, deviceId, password, loginIdentifier } = {}) {
  if (!pin || String(pin).trim().length < 4) {
    throw new Error('El PIN debe tener al menos 4 dígitos.');
  }

  if (!supportsOfflinePinSetup()) {
    throw new Error('Este navegador no permite configurar acceso offline seguro.');
  }

  const baseRecord = await saveOfflineUserProfile(user, { deviceId });
  if (!baseRecord) {
    throw new Error('No se pudo preparar el usuario para acceso offline.');
  }

  const salt = createSaltBase64();
  const pinHash = await derivePinHash(String(pin), salt);
  void password;
  const loginValue = loginIdentifier || user?.login_identifier || user?.email || user?.username || baseRecord.login_identifier || '';

  const updatedRecord = {
    ...baseRecord,
    login_identifier: loginValue,
    can_login_offline: true,
    pin_salt: salt,
    pin_hash: pinHash,
    encrypted_password: null,
    password_iv: null,
    failed_pin_attempts: 0,
    last_failed_pin_attempt_at: null,
    offline_unlock_locked_until: null,
    updated_at: nowIso(),
  };

  await saveRecord(OFFLINE_USER_STORE, updatedRecord);
  return updatedRecord;
}

export async function listOfflineUsersForDevice(deviceId = getOrCreateDeviceId()) {
  await migrateOfflineUserSecurityState();

  const normalizedDeviceId = normalizeId(deviceId);
  if (!normalizedDeviceId) return [];
  return listRecords(OFFLINE_USER_STORE, {
    indexName: 'device_id',
    query: normalizedDeviceId,
  });
}

export async function listOfflineUsersByRestaurant({ restaurantId, deviceId = getOrCreateDeviceId(), onlyEnabled = true } = {}) {
  const records = await listOfflineUsersForDevice(deviceId);
  const normalizedRestaurantId = normalizeId(restaurantId);
  return records
    .filter((record) => record.restaurant_id === normalizedRestaurantId)
    .filter((record) => (onlyEnabled ? record.can_login_offline && record.pin_hash && record.pin_salt : true))
    .sort((first, second) => String(first.display_name || '').localeCompare(String(second.display_name || '')));
}

export async function deleteOfflineUserProfile({ restaurantId, userId, deviceId = getOrCreateDeviceId() } = {}) {
  const normalizedRestaurantId = normalizeId(restaurantId);
  const normalizedUserId = normalizeId(userId);
  const normalizedDeviceId = normalizeId(deviceId);

  if (!normalizedRestaurantId || !normalizedUserId || !normalizedDeviceId) {
    return false;
  }

  const recordId = buildOfflineUserId({
    userId: normalizedUserId,
    restaurantId: normalizedRestaurantId,
    deviceId: normalizedDeviceId,
  });

  return deleteRecord(OFFLINE_USER_STORE, recordId);
}

export async function listOfflineRestaurantsForDevice(deviceId = getOrCreateDeviceId()) {
  const users = await listOfflineUsersForDevice(deviceId);
  const restaurants = new Map();
  users.forEach((user) => {
    if (!user?.restaurant_id || !user?.can_login_offline || !user?.pin_hash) return;
    if (!restaurants.has(user.restaurant_id)) {
      restaurants.set(user.restaurant_id, {
        restaurant_id: user.restaurant_id,
        restaurant_name: user.restaurant_name || '',
      });
    }
  });
  return [...restaurants.values()];
}

export async function verifyOfflineUserPin({ restaurantId, userId, pin, deviceId = getOrCreateDeviceId() } = {}) {
  await migrateOfflineUserSecurityState();

  const normalizedRestaurantId = normalizeId(restaurantId);
  const normalizedUserId = normalizeId(userId);
  const recordId = buildOfflineUserId({
    userId: normalizedUserId,
    restaurantId: normalizedRestaurantId,
    deviceId,
  });

  const userRecord = await getRecord(OFFLINE_USER_STORE, recordId);
  if (!userRecord || !userRecord.can_login_offline || !userRecord.pin_hash || !userRecord.pin_salt) {
    return null;
  }

  if (isOfflineAccessExpired(userRecord)) {
    throw createOfflineAccessError(
      'Este acceso rápido ha caducado. Conéctate a internet e inicia sesión de forma normal para renovarlo.',
      'offline_login_expired',
      { expires_at: buildOfflineAccessExpiry(userRecord) }
    );
  }

  const lockedRemainingMs = getOfflineLockRemainingMs(userRecord);
  if (lockedRemainingMs > 0) {
    throw createOfflineAccessError(
      `Demasiados intentos. Espera ${formatWaitTime(lockedRemainingMs)} antes de volver a introducir el PIN.`,
      'offline_login_locked',
      {
        locked_until: userRecord.offline_unlock_locked_until,
        retry_after_ms: lockedRemainingMs,
      }
    );
  }

  const computedHash = await derivePinHash(String(pin), userRecord.pin_salt);
  if (computedHash !== userRecord.pin_hash) {
    const nextFailedAttempts = Number(userRecord.failed_pin_attempts || 0) + 1;
    const shouldLock = nextFailedAttempts >= OFFLINE_PIN_MAX_ATTEMPTS;
    const lockMultiplier = shouldLock
      ? Math.max(1, nextFailedAttempts - OFFLINE_PIN_MAX_ATTEMPTS + 1)
      : 0;
    const retryAfterMs = shouldLock ? OFFLINE_PIN_LOCK_BASE_MS * lockMultiplier : 0;
    const nextRecord = {
      ...userRecord,
      failed_pin_attempts: nextFailedAttempts,
      last_failed_pin_attempt_at: nowIso(),
      offline_unlock_locked_until: shouldLock ? new Date(Date.now() + retryAfterMs).toISOString() : null,
      updated_at: nowIso(),
    };

    await saveRecord(OFFLINE_USER_STORE, nextRecord);

    if (shouldLock) {
      throw createOfflineAccessError(
        `Demasiados intentos. Espera ${formatWaitTime(retryAfterMs)} antes de volver a introducir el PIN.`,
        'offline_login_locked',
        {
          locked_until: nextRecord.offline_unlock_locked_until,
          retry_after_ms: retryAfterMs,
        }
      );
    }

    throw createOfflineAccessError(
      `PIN incorrecto. Intentos restantes antes del bloqueo: ${OFFLINE_PIN_MAX_ATTEMPTS - nextFailedAttempts}.`,
      'offline_login_invalid_pin',
      {
        remaining_attempts: OFFLINE_PIN_MAX_ATTEMPTS - nextFailedAttempts,
      }
    );
  }

  const updatedRecord = {
    ...userRecord,
    last_offline_login_at: nowIso(),
    failed_pin_attempts: 0,
    last_failed_pin_attempt_at: null,
    offline_unlock_locked_until: null,
    updated_at: nowIso(),
  };
  await saveRecord(OFFLINE_USER_STORE, updatedRecord);
  return updatedRecord;
}

export async function getOfflineUserCredentials(userRecord, pin) {
  if (!userRecord?.encrypted_password || !userRecord?.password_iv || !userRecord?.pin_salt) {
    return null;
  }

  const password = await decryptWithPin({
    encryptedValue: userRecord.encrypted_password,
    pin,
    saltBase64: userRecord.pin_salt,
    ivBase64: userRecord.password_iv,
  });

  return {
    login_identifier: userRecord.login_identifier || '',
    password,
  };
}

export function persistLastActiveRestaurantContext(context = {}) {
  const restaurant_id = normalizeId(context.restaurant_id);
  if (!restaurant_id) return;
  try {
    writeTabContext(LAST_RESTAURANT_CONTEXT_KEY, JSON.stringify({
      restaurant_id,
      restaurant_name: context.restaurant_name || '',
      updated_at: nowIso(),
    }));
  } catch (error) {
    console.warn('Unable to persist EWU last restaurant context:', error);
  }
}

export function getLastActiveRestaurantContext() {
  try { return JSON.parse(readTabContext(LAST_RESTAURANT_CONTEXT_KEY) || 'null'); } catch { return null; }
}

export async function createOfflineSessionFromUser(user) {
  if (!user?.restaurant_id || !user?.user_id) return null;

  const session = {
    mode: 'offline',
    user_id: normalizeId(user.user_id || user.id),
    restaurant_id: normalizeId(user.restaurant_id),
    restaurant_name: user.restaurant_name || '',
    device_id: normalizeId(user.device_id || getOrCreateDeviceId()),
    display_name: user.display_name || user.username || user.email || 'Usuario',
    role: user.role || buildRole(user),
    permissions: normalizeTpvPermissions(user.permissions || user.tpv_permissions, DEFAULT_WAITER_TPV_PERMISSIONS),
    is_partner: Boolean(user.is_partner),
    subscription_plan: user.subscription_plan || 'ewu_tpv',
    user_allowed_to_cancel: Boolean(user.user_allowed_to_cancel),
    started_at: nowIso(),
    source: 'offline_unlock',
  };

  offlineSessionCache = session;
  offlineSessionHydrated = true;
  persistOfflineSession(session);
  persistLastActiveRestaurantContext({
    restaurant_id: session.restaurant_id,
    restaurant_name: session.restaurant_name,
  });
  emitOfflineSessionChanged();
  return session;
}

export function getOfflineSession() {
  if (offlineSessionCache) {
    return cloneSession(offlineSessionCache);
  }

  const storedSession = readJsonStorage(OFFLINE_SESSION_STORAGE_KEY, sessionStorage);
  if (isValidOfflineSession(storedSession)) {
    offlineSessionCache = storedSession;
    offlineSessionHydrated = true;
    return cloneSession(storedSession);
  }

  return null;
}

export function isOfflineSessionActive() {
  const session = getOfflineSession();
  return isValidOfflineSession(session);
}

export async function clearOfflineSession() {
  offlineSessionCache = null;
  offlineSessionHydrated = true;
  removePersistedOfflineSession();
  await removePersistedAppState(OFFLINE_SESSION_STATE_KEY);
  emitOfflineSessionChanged();
}
